import type React from "react";
import { useEffect, useState } from "react";
import { User, ChevronDown, LogOut, Bell, Menu } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../../hooks/useNotifications";
import NotificationDropdown from "../notifications/NotificationDropdown";
import LanguageSwitcher from "../UI/LanguageSwitcher";
import { useTranslation } from "react-i18next";

interface NavbarProps {
  onToggleSidebar: () => void;
  isSidebarCollapsed: boolean;
  isMobile: boolean;
}

const Navbar: React.FC<NavbarProps> = ({
  onToggleSidebar,
  isSidebarCollapsed,
  isMobile,
}) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] =
    useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { unreadCount, refreshUnreadCount } = useNotifications();
  const isDriver = user?.role === "driver";
  const { t } = useTranslation("navigation");

  // The bell is pushed to by SocketBridge (`notification:new`), so the 60s poll
  // that used to live here is gone. This only catches up after the tab has been
  // hidden — the socket may have dropped and events during a gap aren't
  // replayed. Cheap, and it costs nothing while the tab is in the foreground.
  useEffect(() => {
    if (isDriver) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshUnreadCount();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [isDriver, refreshUnreadCount]);

  const handleProfileClick = () => {
    setIsUserMenuOpen(false);
    navigate("/profile");
  };

  const handleLogout = async () => {
    try {
      setIsUserMenuOpen(false);
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleNotificationClick = () => {
    setIsNotificationDropdownOpen(!isNotificationDropdownOpen);
    setIsUserMenuOpen(false);
  };

  const companyName =
    user?.companyId && typeof user.companyId === "object"
      ? user.companyId.name
      : t("defaultOrganization");

  return (
    <header className="bg-[#FAFAF8] border-b border-[rgba(11,15,14,0.08)] shrink-0">
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center space-x-4">
          {/* Mobile Menu Button */}
          {isMobile && (
            <button
              onClick={onToggleSidebar}
              className="p-2 text-[#8A9590] hover:text-[#C97A3C] hover:bg-[#F5F4F0] rounded-lg transition-colors lg:hidden"
              aria-label={
                isSidebarCollapsed
                  ? t("sidebar.openSidebar")
                  : t("sidebar.closeSidebar")
              }
              title={
                isSidebarCollapsed
                  ? t("sidebar.openSidebar")
                  : t("sidebar.closeSidebar")
              }
            >
              <Menu className="h-5 w-5" />
            </button>
          )}
        </div>

        <div className="flex items-center space-x-4">
          {/* Notifications */}
          {!isDriver && (
            <div className="relative">
              <button
                onClick={handleNotificationClick}
                className="relative p-2 text-[#8A9590] hover:text-[#C97A3C] hover:bg-[#F5F4F0] rounded-lg transition-colors"
                aria-label={`${t("user.notifications")} ${unreadCount !== null && unreadCount > 0 ? `(${unreadCount} unread)` : ""}`}
                title={`${t("user.notifications")} ${unreadCount !== null && unreadCount > 0 ? `(${unreadCount} unread)` : ""}`}
              >
                <Bell
                  className="h-5 w-5"
                  aria-label={t("user.notifications")}
                />
                {unreadCount !== null && unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#C97A3C] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>

              <NotificationDropdown
                isOpen={isNotificationDropdownOpen}
                onClose={() => setIsNotificationDropdownOpen(false)}
                onOpenSettings={() => {}}
              />
            </div>
          )}

          {/* Language Switcher */}
          <LanguageSwitcher />

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center space-x-2 p-2 text-[#0B0F0E] hover:text-[#C97A3C] hover:bg-[#F5F4F0] rounded-lg transition-colors"
              aria-label={t("user.userMenu")}
              aria-expanded={isUserMenuOpen}
              aria-haspopup="true"
            >
              <User className="h-5 w-5" />
              <span className="font-medium hidden sm:inline">
                {user?.firstName} {user?.lastName}
              </span>
              <ChevronDown className="h-4 w-4" />
            </button>

            {isUserMenuOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-[rgba(11,15,14,0.1)] py-2 z-50">
                <div className="px-4 py-3 border-b border-[rgba(11,15,14,0.06)]">
                  <p className="font-medium text-[#0B0F0E]">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-sm text-[#8A9590]">{user?.email}</p>
                  <p className="text-xs text-[#8A9590] mt-1">{companyName}</p>
                </div>

                <button
                  onClick={handleProfileClick}
                  className="flex items-center space-x-2 px-4 py-2 text-[#0B0F0E] hover:text-[#C97A3C] hover:bg-[#F5F4F0] w-full text-left transition-colors"
                >
                  <User className="h-4 w-4" />
                  <span>{t("user.profileSettings")}</span>
                </button>

                <hr className="my-2 border-[rgba(11,15,14,0.06)]" />

                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-4 py-2 text-[#0B0F0E] hover:text-[#C97A3C] hover:bg-[#F5F4F0] w-full text-left transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span>{t("user.signOut")}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
